/**
 * App.js
 * 
 * Main application file that handles routing across all pages.
 */

import React from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from "react-router-dom";
import NavBar from "./components/Bars/NavBar";
import Home from "./Screens/Home";
import About from "./Screens/About";
import Contact from "./Screens/Contact";
import "./App.css";
import BottomBar from "./components/Bars/BottomBar";
import GamesRoom from "./Screens/GamesRoom";
import { Toaster } from "react-hot-toast";
import AuctionRoom from "./Games/Auction/Pages/Room";
import AuctionGame from "./Games/Auction/AuctionGamePageHandler";
import ImpostorGamePageHandler from "./Games/TrustIssues/ImpostorGamePageHandler";
import TrustIssuesRoom from "./Games/TrustIssues/Pages/TrustIssuesRoom";
import ForYou from "./Screens/ForYou/ForYou";
import FlyPage from "./Screens/Fly/FlyPage";
import CheckInPage from "./Screens/Fly/CheckInPage";
import CounterPage from "./Screens/Fly/CounterPage";
import ScanPassesPage from "./Screens/Fly/ScanPassesPage";
import ArrivalsPage from "./Screens/Fly/ArrivalsPage";
import AdminPage from "./Screens/Fly/AdminPage";
import ImmigrationPage from "./Screens/Fly/ImmigrationPage";
import LeaderboardPage from "./Screens/Fly/LeaderboardPage";
import RevealRemotePage from "./Screens/Fly/RevealRemotePage";
import { FlyNotFoundPage } from "./Screens/Fly/components/BookingNotFound";
import SouvenirPage from "./Screens/Fly/SouvenirPage";
import ThankYouPage from "./Screens/Fly/ThankYouPage";
import DeclarationsPage from "./Screens/Fly/DeclarationsPage";

/**
 * The site's usual frame: navbar, black background and bottom bar
 */
const SiteLayout = () => (
    <>
        <NavBar />
        <div className="min-h-screen bg-black bg-cover bg-center">
            <Outlet />
        </div>
        <BottomBar />
    </>
);

/**
 * A small "TRIAL RUN" tag on every /fly2 page, so nobody mistakes the trial for the real flight
 */
const TrialBadge = () => {
    const { pathname } = useLocation();
    if (!pathname.startsWith("/fly2")) return null;
    return (
        <div className="fixed left-1/2 -translate-x-1/2 z-[100] pointer-events-none rounded-b-md px-2 py-0.5 text-[9px] font-bold tracking-[0.2em]"
            style={{ top: 0, background: "#F2C230", color: "#1B2A41" }} aria-hidden="true">
            TRIAL RUN
        </div>
    );
};

function App() {
    return (
        <Router>
            <div className="App">
                <Toaster
                    position="top-right"
                    reverseOrder={false}
                />
                <TrialBadge />
                <Routes>
                    {/* Flight YJ030 (the party) stands on its own, without the site's navbar.
                        The fixed pages win over /fly/:pnr, and FlyPage shows "Booking not found" for anything that isn't a PNR. */}
                    {/* The real flight at /fly, and the trial run at /fly2: the same pages, on the backend's trial data */}
                    {["/fly", "/fly2"].map((base) => (
                        <React.Fragment key={base}>
                            <Route path={`${base}/check-in`} element={<CheckInPage />} />
                            <Route path={`${base}/counter`} element={<CounterPage />} />
                            <Route path={`${base}/scan-passes`} element={<ScanPassesPage />} />
                            <Route path={`${base}/arrivals`} element={<ArrivalsPage />} />
                            <Route path={`${base}/admin`} element={<AdminPage />} />
                            <Route path={`${base}/immigration`} element={<ImmigrationPage />} />
                            <Route path={`${base}/leaderboard`} element={<LeaderboardPage />} />
                            <Route path={`${base}/custom-declaration-result`} element={<LeaderboardPage />} />
                            <Route path={`${base}/custom-declaration-result-remote`} element={<RevealRemotePage />} />
                            {/* After the party: each signed-in guest's own boarding pass and passport (real flight only) */}
                            {base === "/fly" && <Route path="/fly/souvenir" element={<SouvenirPage />} />}
                            {base === "/fly" && <Route path="/fly/thank-you" element={<ThankYouPage />} />}
                            {base === "/fly" && <Route path="/fly/customs-declarations" element={<DeclarationsPage />} />}
                            <Route path={`${base}/:pnr`} element={<FlyPage />} />
                            <Route path={`${base}/*`} element={<FlyNotFoundPage />} />
                        </React.Fragment>
                    ))}
                    <Route element={<SiteLayout />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/games" element={<GamesRoom />} />
                        <Route path="/auction" element={<AuctionGame />} />
                        <Route path="/auction/:room" element={<AuctionRoom />} />
                        <Route path="/trust-issues" element={<ImpostorGamePageHandler />} />
                        <Route path="/trust-issues/:room" element={<TrustIssuesRoom />} />
                        <Route path="/for-you" element={<ForYou />} />
                    </Route>
                </Routes>
            </div>
        </Router>
    );
}

export default App;